// entry 로그인 시 기존 저장 토큰을 재사용할지 결정하는 순수 로직.
// landing/index.ts는 앱 전역의 무거운 import 체인을 끌어오므로 단위 테스트가 어렵다.
// 결정 로직만 이 경량 모듈로 분리하여(entryError.ts와 동일한 패턴) CI에서 단위 테스트 가능하게 한다.

// 재사용 직후 첫 API 호출 사이에 토큰이 만료되는 경계 race를 막기 위한 grace 버퍼(초).
export const ENTRY_TOKEN_EXPIRY_GRACE_SECONDS = 60

interface DecodedEntryToken {
  email?: string
}

interface DecodedCommonToken {
  email?: string
  services?: unknown
  exp?: number
}

// reuse: 기존 저장 토큰을 그대로 재사용한다(유효 + 동일 계정).
// exchange: 재사용을 거부하고 entry_token issue-token 교환으로 폴백한다(동일 계정이나 만료/임박).
//           logout()을 호출하지 않아 saga 폴백과의 이중 LOGIN_BY_ENTRY dispatch를 막는다.
// resetThenExchange: 다른 계정/손상 토큰이므로 logout()으로 이전 세션을 정리한 뒤 교환한다.
export type EntryTokenReuseDecision = 'reuse' | 'exchange' | 'resetThenExchange'

// 분기 순서가 정확성에 직결된다: 동일 계정(email 일치 + services 존재) 확정을 만료 검사보다
// 먼저 평가해야 한다. 만료 검사를 먼저 두면 "다른 사용자의 만료 토큰"이 logout 없이 통과되어
// 이전 사용자의 인메모리 상태(JMAP/dataMgr/캘린더/연락처)가 정리되지 않은 채 새 사용자가
// 로그인되는 교차 사용자 데이터 오염이 발생한다.
export function decideEntryTokenReuse(
  parsedEntryToken: DecodedEntryToken | null,
  parsedCommonToken: DecodedCommonToken | null,
  nowMs: number,
  graceSeconds: number = ENTRY_TOKEN_EXPIRY_GRACE_SECONDS,
): EntryTokenReuseDecision {
  if (
    parsedEntryToken == null ||
    parsedCommonToken == null ||
    parsedEntryToken.email !== parsedCommonToken.email ||
    parsedCommonToken.services == null
  ) {
    return 'resetThenExchange'
  }

  // exp가 없으면 만료로 간주하여 안전 측(교환)으로 위임한다.
  const expired =
    parsedCommonToken.exp == null || nowMs > (parsedCommonToken.exp - graceSeconds) * 1000

  // [한계 / 향후 전환 조건]
  // 재사용('reuse')은 stored access_token의 exp(시각)만으로 "유효"를 판정한다.
  // 따라서 exp가 미래여도 서버가 거부하는 토큰 — 예: 서버측 세션 revoke, 비밀번호 변경,
  // 서명 키 교체, 다른 환경에서 발급된 토큰 — 은 'reuse'로 분류되어 그 토큰으로 API를 호출하다
  // 401 → "세션이 만료되었습니다" 로그아웃이 여전히 발생할 수 있다. (exp 기반 판정의 본질적 한계)
  //
  // entry_token은 외부 앱에서 매번 새로 전달되는 신선·권위 자격증명이므로, 위와 같은
  // "exp는 미래지만 무효인 stored token" 사례가 실제로 관측되면, 재사용을 폐기하고
  // entry 진입 시 항상 교환하도록 전환한다 — 즉 이 분기를 `return 'exchange'`(동일 계정도 교환)로
  // 바꾸면 stored token의 만료/무효 모든 경우를 우회한다. 비용은 진입당 issue-token 호출 1회이며,
  // 유효 세션 재진입 시의 재사용 최적화만 포기하면 된다. (현재는 보고된 시간만료 버그만 해결하고
  // 불필요한 교환 호출을 피하기 위해 exp 기반 재사용을 유지한다.)
  return expired ? 'exchange' : 'reuse'
}
